// Handling for the `setUnit` / `cumulSetUnit` URL parameters (user-defined
// unit replacement and scaling of the chart's timeseries data).
// See doc/availableURLParameters.md and
// doc/specs/2026-07-17-setunit-cumulsetunit-flyout-yr.md.

import { getCumulParams } from "./cumulParam"

export type UnitScaleOp = "m" | "a"

export type UnitOverride = {
  /** Unit string with ^/_/=E markup already rendered to Unicode/€. */
  unitString: string
  op: UnitScaleOp
  value: number
}

const SUPERSCRIPT_CHARS: Record<string, string> = {
  "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴",
  "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹",
  "+": "⁺", "-": "⁻", "n": "ⁿ", "i": "ⁱ",
}

const SUBSCRIPT_CHARS: Record<string, string> = {
  "0": "₀", "1": "₁", "2": "₂", "3": "₃", "4": "₄",
  "5": "₅", "6": "₆", "7": "₇", "8": "₈", "9": "₉",
  "+": "₊", "-": "₋",
}

/**
 * Render the unitString markup: `^c` sets c superscript, `_c` sets c
 * subscript (via Unicode equivalents where available), `=E` becomes `€`.
 * E.g. `MtCO_2/Wm^2^2/=E` renders as `MtCO₂/Wm²²/€`.
 */
export function renderUnitString(raw: string): string {
  let result = ""
  for (let i = 0; i < raw.length; i += 1) {
    const marker = raw[i]
    const next = raw[i + 1]
    if (marker === "^" && next !== undefined) {
      result += SUPERSCRIPT_CHARS[next] ?? next
      i += 1
    } else if (marker === "_" && next !== undefined) {
      result += SUBSCRIPT_CHARS[next] ?? next
      i += 1
    } else if (marker === "=" && next === "E") {
      result += "€"
      i += 1
    } else {
      result += marker
    }
  }
  return result
}

/**
 * Parse a `unitString_m_numberString` (or `_a_`) value. The separator is the
 * LAST `_m_` / `_a_` occurrence, since unitString itself may contain `_`
 * subscript markers. Returns null (= behaves like `No`) for values longer
 * than 30 chars, without a valid separator, or without a finite float.
 */
function parseUnitValue(
  raw: string | null,
  allowedOps: UnitScaleOp[]
): UnitOverride | null {
  if (raw === null || raw.length === 0 || raw.length > 30) {
    return null
  }
  let sepIndex = -1
  let op: UnitScaleOp | null = null
  for (const candidate of allowedOps) {
    const idx = raw.lastIndexOf("_" + candidate + "_")
    if (idx > sepIndex) {
      sepIndex = idx
      op = candidate
    }
  }
  if (sepIndex < 1 || op === null) {
    return null
  }
  const value = parseFloat(raw.slice(sepIndex + 3))
  if (!Number.isFinite(value)) {
    return null
  }
  return {
    unitString: renderUnitString(raw.slice(0, sepIndex)),
    op,
    value,
  }
}

/**
 * Resolve `setUnit` from the current URL, or null if unset/invalid.
 * Ignored while `cumul` is active (cumulSetUnit applies there instead).
 */
export function getSetUnitParams(): UnitOverride | null {
  if (typeof window === "undefined") {
    return null
  }
  if (getCumulParams() !== null) {
    return null
  }
  const urlParams = new URLSearchParams(window.location.search)
  const raw = urlParams.get("setUnit") ?? urlParams.get("setunit")
  return parseUnitValue(raw, ["m", "a"])
}

/**
 * Resolve `cumulSetUnit` (multiplier only) from the current URL, or null if
 * unset/invalid. Only applied while `cumul` is active.
 */
export function getCumulSetUnitParams(): UnitOverride | null {
  if (typeof window === "undefined") {
    return null
  }
  if (getCumulParams() === null) {
    return null
  }
  const urlParams = new URLSearchParams(window.location.search)
  const raw = urlParams.get("cumulSetUnit") ?? urlParams.get("cumulsetunit")
  return parseUnitValue(raw, ["m"])
}

/** Whichever of setUnit / cumulSetUnit applies given the cumul state. */
export function getActiveUnitOverride(): UnitOverride | null {
  return getSetUnitParams() ?? getCumulSetUnitParams()
}

/** Apply the override's scaling operation to a single data value. */
export function applyUnitScale(value: number, override: UnitOverride): number {
  return override.op === "a" ? value + override.value : value * override.value
}

/**
 * Replace the VarUnit inside the trailing `(...)` of the y-axis label with the
 * active override's unitString. No-op when no override is active or the label
 * has no trailing parenthesized unit.
 */
export function applyUnitToYLabel(label: string): string {
  const override = getActiveUnitOverride()
  if (override === null) {
    return label
  }
  return label.replace(
    /\(([^()]*)\)(\s*)$/,
    (_match, _unit, trailing) => "(" + override.unitString + ")" + trailing
  )
}
